import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Stethoscope, LayoutDashboard, Users, Upload, LogOut, ShieldCheck } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/patients", label: "Patients", icon: Users, testid: "nav-patients" },
  { to: "/upload", label: "New Report", icon: Upload, testid: "nav-upload" },
  { to: "/audit", label: "Audit log", icon: ShieldCheck, testid: "nav-audit" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-canvas)" }}>
      <aside className="w-64 border-r flex-shrink-0 hidden md:flex flex-col" style={{ borderColor: "var(--color-border)", background: "var(--bg-surface)" }}>
        <Link to="/dashboard" data-testid="brand-link" className="px-6 py-6 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-semibold text-[17px] leading-none" style={{ color: "var(--color-text-primary)" }}>MedNarrative</div>
            <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>Imaging in plain English</div>
          </div>
        </Link>

        <nav className="px-3 flex-1 space-y-1">
          {navItems.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#2A4736] text-white"
                    : "text-[#4A534F] hover:bg-[#F9F8F6]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t p-4 flex items-center gap-3" style={{ borderColor: "var(--color-border)" }}>
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ background: "var(--color-secondary)" }}>
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate" style={{ color: "var(--color-text-primary)" }}>{user?.name}</div>
            <div className="text-xs truncate" style={{ color: "var(--color-text-secondary)" }}>{user?.email}</div>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="p-2 rounded-md hover:bg-[#F9F8F6]" title="Sign out">
            <LogOut className="w-4 h-4" style={{ color: "var(--color-text-secondary)" }} />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden border-b px-4 py-3 flex items-center justify-between" style={{ borderColor: "var(--color-border)", background: "var(--bg-surface)" }}>
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold">MedNarrative</span>
          </Link>
          <button data-testid="logout-button-mobile" onClick={handleLogout} className="text-sm" style={{ color: "var(--color-text-secondary)" }}>Sign out</button>
        </div>
        <div className="px-5 md:px-10 py-8 md:py-12 max-w-7xl mx-auto fade-up">
          {children}
        </div>
      </main>
    </div>
  );
};
